/**
 * Mirror of the Central-API response envelope and stats payloads. Keep in
 * sync with `Central-API/Routes/statsApi.route.js` + `service/cacheService.js`.
 */

export type ApiType = "PAN" | "CRIF" | "UAN";
export type DataSource = "CACHE" | "THIRD_PARTY" | "MOCK" | null;
export type Provider =
  | "DIGITAP"
  | "CRIF"
  | "CRIF_MOCK"
  | "SIGNZY"
  | "INTERNAL"
  | "MOCK"
  | string;

export interface ApiEnvelope<T> {
  status_code: number;
  success: boolean;
  response: T;
  cache_hit: boolean;
  data_source: DataSource;
  message: string;
}

export interface ErrorEnvelope {
  status_code: number;
  success: false;
  code: string;
  message: string;
  details?: unknown;
  cache_hit: false;
  data_source: null;
  request_id?: string;
}

// ── /stats/overview ──────────────────────────────────────────────

export interface OverviewTotals {
  total_requests: number;
  success: number;
  errors: number;
  cache_hits: number;
  upstream_calls: number;
}

export interface OverviewRates {
  success_pct: number;
  error_pct: number;
  cache_hit_pct: number;
  third_party_dependency_pct: number;
}

export interface OverviewTiming {
  avg_response_time_ms: number;
  max_response_time_ms: number;
  avg_upstream_ms: number;
}

export interface ApiBreakdownRow {
  _id: ApiType | string;
  total: number;
  success: number;
  errors: number;
  cache_hits: number;
  upstream_calls: number;
  avg_response_time_ms: number;
}

export interface BrandBreakdownRow {
  _id: string;
  total: number;
  success: number;
  errors: number;
  cache_hits: number;
  avg_response_time_ms: number;
}

export interface StatusRow {
  _id: number;
  count: number;
}

export interface DataSourceRow {
  _id: DataSource;
  count: number;
  avg_response_time_ms: number;
}

export interface OverviewResponse {
  window: string;
  totals: OverviewTotals;
  rates: OverviewRates;
  timing: OverviewTiming;
  by_api_type: ApiBreakdownRow[];
  by_brand: BrandBreakdownRow[];
  by_status: StatusRow[];
  by_data_source: DataSourceRow[];
}

// ── /stats/cache ─────────────────────────────────────────────────

export interface CacheCountRow {
  _id: { api_type: ApiType | string; brand_id: string };
  docs: number;
  total_hits: number;
  avg_hits: number;
  avg_ttl_days: number;
}

export interface HotPanRow {
  _id: string;
  pan: string;
  api_type: ApiType | string;
  brand_id: string;
  provider: Provider | null;
  hit_count: number;
  ttl_days: number;
  expires_at: string;
  last_accessed_at: string | null;
  created_at: string;
}

export interface RecentMissRow {
  _id: string;
  brand_id: string;
  api_type: ApiType | string;
  pan: string | null;
  response_time_ms: number;
  upstream_provider: Provider | null;
  created_at: string;
}

export interface CacheResponse {
  counts_by_api_brand: CacheCountRow[];
  hot_pans: HotPanRow[];
  recent_cache_misses: RecentMissRow[];
}

// ── /stats/errors ────────────────────────────────────────────────

export interface ErrorByCodeRow {
  _id: string | null;
  count: number;
}

export interface ErrorByApiRow {
  _id: ApiType | string;
  count: number;
  unique_codes: (string | null)[];
}

export interface RecentErrorRow {
  _id: string;
  brand_id: string;
  api_type: ApiType | string;
  pan: string | null;
  response_status: number;
  error_code: string | null;
  error_message: string | null;
  upstream_provider: Provider | null;
  response_time_ms: number;
  created_at: string;
}

export interface CircuitState {
  name: string;
  state: "CLOSED" | "OPEN" | "HALF_OPEN";
  failures: number;
  threshold: number;
  opened_at: string | null;
  cooldown_ms_remaining: number;
}

export interface ErrorsResponse {
  window: string;
  by_code: ErrorByCodeRow[];
  by_api_type: ErrorByApiRow[];
  recent: RecentErrorRow[];
  circuits: CircuitState[];
}

// ── /stats/brand/:id ─────────────────────────────────────────────

export interface BrandTotals {
  total: number;
  success: number;
  errors: number;
  cache_hits: number;
  upstream_calls: number;
  avg_response_time_ms: number;
}

export interface BrandApiRow {
  _id: ApiType | string;
  total: number;
  errors: number;
  cache_hits: number;
  avg_response_time_ms: number;
}

export interface BrandTopPanRow {
  _id: { pan: string; api_type: ApiType | string };
  count: number;
  cache_hits: number;
  last_seen: string;
}

export interface BrandRecentErrorRow {
  _id: string;
  api_type: ApiType | string;
  pan: string | null;
  response_status: number;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
}

export interface BrandResponse {
  brand_id: string;
  window: string;
  totals: BrandTotals;
  rates: { success_pct: number; error_pct: number; cache_hit_pct: number };
  by_api_type: BrandApiRow[];
  top_pans: BrandTopPanRow[];
  recent_errors: BrandRecentErrorRow[];
}

// ── /stats/circuits ──────────────────────────────────────────────

export interface CircuitsResponse {
  circuits: CircuitState[];
}

// ── /api/v1/fetch (used by Tester page) ──────────────────────────

export interface FetchRequestBody {
  brand_id: string;
  pan: string;
  api_type: ApiType;
  payload: Record<string, unknown>;
  cache_ttl_days: number;
}

export interface FetchResponseBody {
  pan: string;
  api_type: ApiType;
  brand_id: string;
  provider: Provider | null;
  ttl_days: number;
  fetched_at: string;
  expires_at: string;
  cache_age_ms: number;
  hit_count: number;
  data: { normalized: unknown; raw: unknown };
}

// ── Auth ─────────────────────────────────────────────────────────

export interface User {
  id: string;
  name: string;
  email: string;
  role: "ADMIN" | "USER";
  brand_ids: string[];
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface BrandRecord {
  _id: string;
  brand_id: string;
  brand_name: string;
  api_key: string;
  allowed_apis: string[];
  rate_limit_per_min: number;
  rate_limit_per_day: number;
  status: "ACTIVE" | "DISABLED";
  created_at: string;
  updated_at: string;
}

export interface BrandListResponse {
  brands: BrandRecord[];
}

export const TIME_WINDOWS = ["1h", "6h", "24h", "7d", "30d", "all"] as const;
export type TimeWindow = (typeof TIME_WINDOWS)[number];
